import { FC } from 'react';
import cn from 'classnames';
import styles from './drawer.module.scss';
import { SxmLogoWaterMark } from '@/assets/icons/SxmLogoWaterMark';
import { CloseIcon } from '@/assets/icons/CloseIcon';
import { ChevronIcon } from '@/assets/icons/ChevronIcon';
import { Button } from '@/ui-components/atoms/Button/Button';
import { Link } from '@/ui-components/atoms/Link/Link';
import { AppDownloadButtons } from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { NavDropdownSectionMobile } from '../navItems/NavDropdown/NavDropdownSectionMobile';
import { NavDropdownContentProps, NavItem, PopoverMenuProps, HelpCenterItem } from '../NavHeader.type';
import { DownloadButtonProps } from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { MobileNavItems } from './MobileNavItems';

interface MobileDrawerProps {
    isSimpleType: boolean;
    drawerOpen: boolean;
    setDrawerOpen: (val: boolean) => void;
    handleDrawerToggle: () => void;
    navLeftItems?: (NavItem | NavDropdownContentProps | PopoverMenuProps)[];
    supportTitle?: string;
    setActiveInnerDrawerIndex: (val: number | null) => void;
    activeInnerDrawerIndex: number | null;
    navDropdownContentMobile?: {
        listItems: { linkText: string; href: string }[];
        tileContent: React.ReactNode;
    };
    helpCenterItem?: HelpCenterItem;
    trySiriusXMlink?: string;
    navRightCtas?: NavItem[];
    appdownloadItem?: DownloadButtonProps[];
}

const HELP_CENTER_INNER_DRAWER_INDEX = -1;

function isPopoverMenuProps(item: NavItem | NavDropdownContentProps | PopoverMenuProps): item is PopoverMenuProps {
    return (
        typeof (item as PopoverMenuProps)?.mainHeading !== 'undefined' ||
        typeof (item as PopoverMenuProps)?.linkSet !== 'undefined'
    );
}

export const MobileDrawer: FC<MobileDrawerProps> = ({
    isSimpleType,
    drawerOpen,
    setDrawerOpen,
    handleDrawerToggle,
    navLeftItems,
    supportTitle,
    setActiveInnerDrawerIndex,
    activeInnerDrawerIndex,
    navDropdownContentMobile,
    helpCenterItem,
    trySiriusXMlink,
    navRightCtas,
    appdownloadItem,
}) => {
    if (isSimpleType) return null;

    return (
        <div className={cn(styles.drawer, { [styles.open]: drawerOpen })} data-componenttype="Global Nav">
            <div className={styles.closeButtonWrapper}>
                <button
                    className={styles.closeButton}
                    onClick={() => {
                        setDrawerOpen(false);
                        handleDrawerToggle();
                    }}
                    aria-label="Close"
                >
                    <CloseIcon />
                </button>
            </div>

            {navLeftItems && (
                <ul className={styles.navList}>
                    {navLeftItems.map((item, index) => {
                        if (isPopoverMenuProps(item) && item.mainHeading) {
                            return (
                                <li key={`popover-${index}`}>
                                    <button
                                        className={styles.mobileSupportButton}
                                        onClick={() => setActiveInnerDrawerIndex(index)}
                                        aria-label={item.mainHeading}
                                        type="button"
                                    >
                                        <span>{item.mainHeading}</span>
                                        <span className={styles.chevronIconWrapper}>
                                            <ChevronIcon direction="right" />
                                        </span>
                                    </button>
                                </li>
                            );
                        }
                        return (
                            <MobileNavItems
                                key={index}
                                index={index}
                                item={item}
                                setActiveInnerDrawerIndex={setActiveInnerDrawerIndex}
                            />
                        );
                    })}
                </ul>
            )}

            {supportTitle && (
                <button
                    className={styles.mobileSupportButton}
                    onClick={() => setActiveInnerDrawerIndex(HELP_CENTER_INNER_DRAWER_INDEX)}
                    aria-label="Support"
                >
                    <span>{supportTitle}</span>
                    <span className={styles.chevronIconWrapper}>
                        <ChevronIcon direction="right" />
                    </span>
                </button>
            )}

            <div
                className={cn(styles.drawer, styles.innerDrawer, {
                    [styles.open]: activeInnerDrawerIndex !== null,
                })}
            >
                <div className={styles.innerDrawerHeader}>
                    <button className={styles.chevronIcon} onClick={() => setActiveInnerDrawerIndex(null)}>
                        <ChevronIcon direction="left" />
                    </button>
                    <button
                        className={styles.closeIconButton}
                        onClick={() => {
                            setActiveInnerDrawerIndex(null);
                            setDrawerOpen(false);
                            handleDrawerToggle();
                        }}
                        aria-label="Close"
                    >
                        <CloseIcon />
                    </button>
                </div>

                {navDropdownContentMobile && (
                    <NavDropdownSectionMobile navDropdownContentMobile={navDropdownContentMobile} />
                )}

                {navLeftItems &&
                    navLeftItems.map((item, index) => {
                        if (!(isPopoverMenuProps(item) && item.mainHeading)) return null;
                        if (activeInnerDrawerIndex !== index) return null;

                        return (
                            <ul key={`inner-popover-${index}`} className={styles.navList}>
                                {item.mainHeading && item.mainHeadingLink && (
                                    <li className={styles.navItem}>
                                        <Link href={item.mainHeadingLink} className={styles.navLink}>
                                            {item.mainHeading}
                                        </Link>
                                    </li>
                                )}
                                {item.linkSet?.links?.map((lnk, liIndex) => (
                                    <li key={liIndex} className={styles.navItem}>
                                        {lnk.href && (
                                            <Link href={lnk.href} className={styles.navLink}>
                                                {lnk.children}
                                            </Link>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        );
                    })}

                {helpCenterItem && activeInnerDrawerIndex === HELP_CENTER_INNER_DRAWER_INDEX && (
                    <ul className={styles.navList}>
                        {helpCenterItem.mainHeading && helpCenterItem.mainHeadingLink && (
                            <li key="main-heading" className={styles.navItem}>
                                <Link href={helpCenterItem.mainHeadingLink} className={styles.navLink}>
                                    {helpCenterItem.mainHeading}
                                </Link>
                            </li>
                        )}
                        {helpCenterItem.linkSet?.links?.map((item, index) => (
                            <li key={index} className={styles.navItem}>
                                {item.href && (
                                    <Link href={item.href} className={styles.navLink}>
                                        {item.children}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {trySiriusXMlink && (
                <div className={styles.mobileButtonWrapper}>
                    <Button
                        variant="primary"
                        type="button"
                        colorSet="base"
                        size="small"
                        fitting="hug-contents"
                        href={trySiriusXMlink}
                        aria-label="Try SiriusXM"
                    >
                        Try SiriusXM
                    </Button>
                </div>
            )}
            <ul className={styles.navList}>
                {navRightCtas?.map((item, index) =>
                    item.link && item.link_type === 'Text' ? (
                        <MobileNavItems
                            key={index}
                            index={index}
                            item={item}
                            setActiveInnerDrawerIndex={setActiveInnerDrawerIndex}
                        />
                    ) : (
                        item.link && (
                            <li key={index}>
                                <div key={index} className={styles.mobileButtonWrapper}>
                                    <Button
                                        key={index}
                                        variant="primary"
                                        type="button"
                                        colorSet="base"
                                        size="large"
                                        fitting="hug-contents"
                                        aria-label={item.aria_label}
                                        href={item.link}
                                    >
                                        {item.title}
                                    </Button>
                                </div>
                            </li>
                        )
                    ),
                )}
            </ul>

            <div
                className={cn({
                    [styles.appDownloadAndLogo]: activeInnerDrawerIndex === null,
                    [styles.appDownloadAndLogoHide]: activeInnerDrawerIndex !== null,
                })}
            >
                <div className={styles.sxmLogoWaterMark}>
                    <SxmLogoWaterMark />
                </div>
                {appdownloadItem && (
                    <div className={styles.appDownload}>
                        <AppDownloadButtons items={appdownloadItem} />
                    </div>
                )}
            </div>
        </div>
    );
};
